import 'highlight.js/styles/github-dark.css';
import 'quill/dist/quill.snow.css';
import hljs from 'highlight.js/lib/common';

// Make highlight.js available globally for Quill syntax module
if (typeof window !== 'undefined') {
    window.hljs = hljs;
}
if (typeof globalThis !== 'undefined') {
    globalThis.hljs = hljs;
}

const INSTANCE_KEY = '__noteQuillInstance';
let quillLoader = null;

async function uploadFileAndInsert(quill, file, type) {
    const noteId = window.location.pathname.split('/').pop(); // Assuming URL is /notes/{id}/edit
    const formData = new FormData();
    formData.append('file', file);
    formData.append('_token', document.querySelector('meta[name="csrf-token"]').content);

    try {
        const response = await fetch(`/notes/${noteId}/upload`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Upload failed');
        }

        const data = await response.json();
        const range = quill.getSelection();
        const index = range ? range.index : quill.getLength();

        if (type === 'image') {
            quill.insertEmbed(index, 'image', data.url);
        } else if (type === 'video') {
            quill.insertEmbed(index, 'video', data.url);
        }
    } catch (error) {
        console.error('File upload failed:', error);
        alert('Upload failed. Please try again.');
    }
}

async function loadQuill() {
    if (!quillLoader) {
        quillLoader = Promise.all([
            import('quill'),
            import('quill/modules/syntax'),
        ])
            .then(([quillModule, syntaxModule]) => {
                const Quill = quillModule.default || quillModule;
                const Syntax = syntaxModule.default || syntaxModule;
                Quill.register('modules/syntax', Syntax, true);
                return Quill;
            })
            .catch((error) => {
                console.error('Quill initialization failed', error);
                throw error;
            });
    }

    return quillLoader;
}

function syncHiddenInput(root) {
    const state = root[INSTANCE_KEY];
    if (!state || !state.quill || !state.input) {
        return;
    }

    const html = state.quill.root.innerHTML;
    state.input.value = html === '<p><br></p>' ? '' : html;
}

function initEditor(root) {
    if (!root || root[INSTANCE_KEY]) {
        return;
    }

    const toolbar = root.querySelector('[data-note-quill-toolbar]');
    const editorContainer = root.querySelector('[data-note-quill-editor]');
    const input = root.querySelector('[data-note-quill-input]');

    if (!toolbar || !editorContainer || !input) {
        return;
    }

    const state = { quill: null, input, syncHandler: null };
    root[INSTANCE_KEY] = state;

    loadQuill()
        .then((Quill) => {
            const quill = new Quill(editorContainer, {
                theme: 'snow',
                placeholder: editorContainer.dataset.placeholder || 'Zacznij pisać swoją notatkę...',
                modules: {
                    toolbar: {
                        container: toolbar,
                    handlers: {
                        undo() {
                            this.quill.history.undo();
                        },
                        redo() {
                            this.quill.history.redo();
                        },
                        image() {
                            const input = document.createElement('input');
                            input.setAttribute('type', 'file');
                            input.setAttribute('accept', 'image/*');
                            input.click();

                            input.onchange = async () => {
                                const file = input.files[0];
                                if (file) {
                                    await uploadFileAndInsert(this.quill, file, 'image');
                                }
                            };
                        },
                        video() {
                            const input = document.createElement('input');
                            input.setAttribute('type', 'file');
                            input.setAttribute('accept', 'video/*');
                            input.click();

                            input.onchange = async () => {
                                const file = input.files[0];
                                if (file) {
                                    await uploadFileAndInsert(this.quill, file, 'video');
                                }
                            };
                        },
                    },
                },
                history: {
                    delay: 1000,
                    maxStack: 200,
                    userOnly: true,
                },
                clipboard: {
                    matchVisual: false,
                },
                keyboard: {
                    bindings: {
                        indent: {
                            key: 9,
                            handler(range, context) {
                                if (context.format.list) {
                                    quill.format('indent', '+1');
                                    return false;
                                }
                                return true;
                            },
                        },
                        outdent: {
                            key: 9,
                            shiftKey: true,
                            handler(range, context) {
                                if (context.format.indent) {
                                    quill.format('indent', '-1');
                                    return false;
                                }
                                return true;
                            },
                        },
                    },
                },
                syntax: {
                    highlight: (text) => window.hljs.highlightAuto(text).value,
                },
            },
        });

        state.quill = quill;

        const initialValue = input.value?.trim();
        if (initialValue) {
            quill.clipboard.dangerouslyPasteHTML(initialValue);
            quill.history.clear();
        }

        const syncHandler = () => syncHiddenInput(root);
        state.syncHandler = syncHandler;

        root.addEventListener('note-quill:sync', syncHandler);

        quill.on('text-change', () => {
            syncHiddenInput(root);
            document.dispatchEvent(new CustomEvent('editor-content-changed'));
            if (typeof window.__noteAutosave === 'function') {
                window.__noteAutosave();
            }
        });

            syncHiddenInput(root);
        })
        .catch((error) => {
            console.error('Quill initialization failed', error);
            delete root[INSTANCE_KEY];
        });
}

function initAllEditors() {
    document.querySelectorAll('[data-note-quill-root]').forEach((root) => initEditor(root));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllEditors);
} else {
    initAllEditors();
}

const observer = new MutationObserver(() => {
    initAllEditors();
});

observer.observe(document.body, {
    childList: true,
    subtree: true,
});

export { initAllEditors };
